<template>
  <div>
    <vx-card class="mb-4">
      <SearchYear
        label="실시내역"
        :query="{ y: 'year' }"
        :options="{
          default: new Date().getFullYear(),
          afterFix: '년',
        }"
        v-model="selectedYear"
      />
    </vx-card>
    <div class="flex items-center justify-end mb-4">
      <vs-button color="secondary" type="border" @click="onClickExcelDownload">Excel 다운로드</vs-button>
    </div>
    <div class="w-full overflow-x-auto">
      <table>
        <colgroup>
          <col width="100" />
          <col width="40" />
          <col width="40" />
          <col width="40" />
          <col width="40" />
          <col width="40" />
          <col width="40" />
          <col width="40" />
          <col width="40" />
          <col width="40" />
          <col width="40" />
          <col width="40" />
          <col width="40" />
          <col width="110" />
        </colgroup>
        <thead>
          <tr>
            <th>구분</th>
            <th v-for="_ in 12" :key="`month-${_}`">{{ _ }}월</th>
            <th>사업장별 조치율<br /><small class="muted">(올해 전체)</small></th>
          </tr>
        </thead>
        <tbody v-if="loading">
          <tr>
            <td colspan="14">
              <div class="w-full p-5 flex items-center justify-center" style="min-height: 100px">
                <feather-icon icon="LoaderIcon" class="mr-2 rotate-icon" />
                데이터를 불러오는 중입니다.
              </div>
            </td>
          </tr>
        </tbody>
        <tbody v-else-if="!datas.length">
          <tr>
            <td colspan="14">
              <div class="w-full p-5 flex items-center justify-center" style="min-height: 100px">
                <feather-icon icon="AlertTriangleIcon" class="mr-2" />
                표시할 데이터가 없습니다.
              </div>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr v-for="item in datas" :key="item.label">
            <td>{{ item.label }}</td>
            <td v-for="(_, _i) in item.data" :key="`${item.label}-${_i}`">
              <div class="flex items-center justify-center">
                <div class="circle" :class="_ ? 'green' : ''"></div>
              </div>
            </td>
            <td class="pct">{{ item.rate }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import SearchYear from "@/views/list/components/SearchYear.vue";
import XLSX from "xlsx";
import { normalizeOpinions, opinionStats, addStats, emptyStats, actionRate, sortSites } from "@/views/boards/single-page/817/opinion.js";

export default {
  components: { SearchYear },
  data() {
    return {
      selectedYear: "", // 초기 선택된 연도를 현재 연도로 설정
      datas: [], // 사업장순서대로 정렬된 배열
      loading: false,
    };
  },
  watch: {
    selectedYear(value) {
      this.checkMonth();
    },
  },
  created() {
    this.checkMonth();
  },
  methods: {
    onClickExcelDownload() {
      // 새로운 워크북 생성
      const wb = XLSX.utils.book_new();

      // 데이터를 추가할 워크시트 생성
      const ws_data = [["구분", "1월", "2월", "3월", "4월", "5월", "6월", "7월", "8월", "9월", "10월", "11월", "12월", "사업장별 조치율(올해 전체)"]];

      // datas 는 배열. for...in 은 Array.prototype 에 붙은 remove/removeIndex 까지 돌아 오류가 나므로 for...of 로 돈다
      for (const site of this.datas) {
        const _data = site.data.reduce(
          (prev, curr) => {
            return [...prev, curr ? "작성" : "미작성"];
          },
          [site.label]
        );
        _data.push(site.rate);
        ws_data.push(_data);
      }

      // 워크시트 데이터 추가
      const ws = XLSX.utils.aoa_to_sheet(ws_data);

      // 워크북에 워크시트 추가
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

      // 엑셀 파일 다운로드
      XLSX.writeFile(wb, `${this.selectedYear} 크레인안전협의체 실시내역.xlsx`);
    },
    async checkMonth() {
      if (!this.selectedYear) return;

      const projectId = this.$route.params.proj_id;
      const boardId = this.$route.params.boardId;
      const page = 1;
      const perPage = 10000;
      const dateTarget = "body.monthDate";
      let dateBegin = `${this.selectedYear}-01-01`;
      let dateEnd = `${this.selectedYear}-12-31`;

      const conditions = [];
      conditions.push({ key: dateTarget, operator: ">=", value: dateBegin });
      conditions.push({ key: dateTarget, operator: "<=", value: dateEnd });

      this.loading = true;
      try {
        // 화면 틀(ProjectLayout)의 팝업 공지 목록 등 다른 LOAD_POST_LIST 와 동시에 돌기 때문에
        // 공용 store 상태(postListInfo)가 아니라 이 요청의 응답값을 쓴다. 아니면 나중에 끝난 다른 목록을 읽어 표가 비어 보인다.
        const response = await this.$store.dispatch("board/LOAD_POST_LIST", { projectId, boardId, page, perPage, conditions });
        const list = (response && response.data) || [];

        let _datas = {};

        if (0 < list.length) {
          list.map((data) => {
            const filedId = data.field_id;
            const content = JSON.parse(data.post_content);
            if (!_datas[filedId]) {
              _datas[filedId] = {
                label: data.field.field_name,
                data: [false, false, false, false, false, false, false, false, false, false, false, false],
                stats: emptyStats(),
              };
            }
            _datas[filedId].data[Number(content.header.month) - 1] = true;
            addStats(_datas[filedId].stats, opinionStats(normalizeOpinions(content.body && content.body.subInfo).의견목록));
          });

          // 조치율 = (완료+기타) ÷ 전체 의견, 올해 전체 기준. 특이사항 없음 회의는 의견이 없어 빠진다.
          for (const key in _datas) _datas[key].rate = actionRate(_datas[key].stats);
          this.datas = sortSites(Object.values(JSON.parse(JSON.stringify(_datas))));
        } else {
          this.datas = [];
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;

  &,
  th,
  td {
    font-size: 14px;
    text-align: center;
    background: white;
    border: 1px solid #cdcdcd;
    padding: 18px 0;
  }

  th {
    font-weight: 500;
    background: #fafafa;
  }

  td {
    font-weight: 400;
  }
}

.circle {
  width: 10px;
  height: 10px;
  background: #ea5455;
  border-radius: 100%;

  &.green {
    background: #28c76f;
  }
}
</style>
